// Helpers for the two monthly recurrence picker options. The BE infers the
// position / weekday / last-day flag from the visit's start date, so the FE
// only needs to send `monthlyMode`. These helpers compute the inputs we need
// for the dropdown labels.

const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export type WeekdayKey = typeof WEEKDAY_KEYS[number];

export type MonthlyDayOfWeekPosition = 1 | 2 | 3 | 4 | -1;

export interface MonthlyDayOfWeekDescriptor {
  position: MonthlyDayOfWeekPosition;
  weekday: WeekdayKey;
}

export const getMonthlyDayOfWeekDescriptor = (startDate: Date): MonthlyDayOfWeekDescriptor => {
  const dayOfMonth = startDate.getDate();
  const lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
  const isLastOccurrenceInMonth = dayOfMonth + 7 > lastDayOfMonth;
  const weekday = WEEKDAY_KEYS[startDate.getDay()];

  if (isLastOccurrenceInMonth) {
    return { position: -1, weekday };
  }
  const position = (Math.floor((dayOfMonth - 1) / 7) + 1) as Exclude<MonthlyDayOfWeekPosition, -1>;
  return { position, weekday };
};

// "Monthly on the 30th"/"31st" auto-promotes to "last day" so the BE uses
// BYMONTHDAY=-1 and the visit lands on the last day of every month (incl. Feb).
export const isMonthlyOnLastDay = (startDate: Date): boolean => {
  const dayOfMonth = startDate.getDate();
  return dayOfMonth === 30 || dayOfMonth === 31;
};

// English ordinal formatter — "1st", "2nd", "15th", "23rd". Non-English locales
// use the same English ordinals as placeholder text (per VIS-1080 spec).
export const formatOrdinalEn = (n: number): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

export const POSITION_MESSAGE_IDS: Record<1 | 2 | 3 | 4, string> = {
  1: 'createVisitFormV2.whenStep.repeat.ordinals.first',
  2: 'createVisitFormV2.whenStep.repeat.ordinals.second',
  3: 'createVisitFormV2.whenStep.repeat.ordinals.third',
  4: 'createVisitFormV2.whenStep.repeat.ordinals.fourth',
};
